import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import * as moment from 'moment';
import { NzI18nInterface, NzI18nService } from 'ng-zorro-antd/i18n';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { CalendarService } from 'src/app/_services/calendar.service';
import { CalendarRequestType } from 'src/app/models/calendar-request-type';
import { UtilityService } from 'src/app/_services/utility.service';
import {
  CALENDAR_REQUEST_UI,
  DEFAULT_REQUEST_UI
} from '../calendar-request.ui';

@Component({
  selector: 'app-user-calendar',
  templateUrl: './user-calendar.component.html',
  styleUrls: ['./user-calendar.component.css']
})
export class UserCalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly calendarService: CalendarService = inject(CalendarService);
  private readonly modalRef: NzModalRef = inject(NzModalRef);
  private readonly i18n: NzI18nService = inject(NzI18nService);
  public readonly utilityService: UtilityService = inject(UtilityService);

  @ViewChild('datePicker', { read: ElementRef }) datePicker!: ElementRef;
  private readonly originalLocale!: NzI18nInterface;
  monthSelected: Date = new Date();
  chunkedDays: any[];

  destroy$: Subject<void> = new Subject<void>();
  readonly modalData: any = inject(NZ_MODAL_DATA); // dati in input passati dalla modale
  user: any;

  events: any[] = [
    {
      label: 'Festività',
      img: 'assets/images/icon_calendar/holiday_s.svg',
      id: 8,
      value: false
    },
    {
      label: 'Sprint review',
      img: 'assets/images/icon_calendar/sprint_review_s.svg',
      id: 6,
      value: false
    },
    {
      label: 'Permesso',
      img: 'assets/images/icon_calendar/permission_s.svg',
      id: 5,
      value: false
    },
    {
      label: 'Ferie',
      img: 'assets/images/icon_calendar/vacation_s.svg',
      id: 4,
      value: false
    },
    {
      label: 'Malattia',
      img: 'assets/images/icon_calendar/sickness_s.svg',
      id: 3,
      value: false
    },
    {
      label: 'Review',
      img: 'assets/images/icon_calendar/review_s.svg',
      id: 1,
      value: false
    },
    {
      label: 'Remoto',
      img: 'assets/images/icon_calendar/smart_s.svg',
      id: 2,
      value: false
    },
    {
      label: 'In pending',
      img: 'assets/images/icon_calendar/icon_pending.svg',
      id: 0,
      value: false
    }
  ];

  constructor() {}

  ngOnInit() {
    this.user = this.modalData;
    this.getUserCalendar();
  }

  ngAfterViewInit() {
    this.updateHeaderLabel();
  }

  getUserCalendar(month?: any) {
    this.calendarService
      .getUserCalendar(this.user.id, month)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.chunkedDays = this.chunkDays(res.users.days);
        }
      });
  }

  closeModal(): void {
    this.modalRef.close();
    this.i18n.setLocale(this.originalLocale);
  }

  /**
   * Funzione che serve per andare inidetro di un mese
   */
  previousMonth(): void {
    const year = this.monthSelected.getFullYear();
    const month = this.monthSelected.getMonth();
    this.monthSelected = new Date(year, month - 1, 1); // torna al mese precedente
    this.updateHeaderLabel();
    this.getUserCalendar(moment(this.monthSelected).format('YYYY-MM-DD'));
  }

  /**
   * Funzione che serve per andare avanti di un mese
   */
  nextMonth(): void {
    const year = this.monthSelected.getFullYear();
    const month = this.monthSelected.getMonth();
    this.monthSelected = new Date(year, month + 1, 1); // vai al mese successivo
    this.updateHeaderLabel();
    this.getUserCalendar(moment(this.monthSelected).format('YYYY-MM-DD'));
  }

  /**
   * Questa funzione serve per formattare la label sul data picker, in maniera tale che vangato tornato
   * Mese anno
   * @returns string Mese anno. Es: Maggio 2025
   */
  updateHeaderLabel() {
    if (!this.datePicker) return;
    const headerEl = this.datePicker.nativeElement.querySelector(
      '.ant-picker-header-view'
    );

    if (headerEl) {
      const formatted = moment(this.monthSelected)
        .locale('it')
        .format('MMMM YYYY');
      headerEl.setAttribute(
        'data-label',
        formatted.charAt(0).toUpperCase() + formatted.slice(1)
      );
    }
  }

  get formattedMonth(): string {
    const text = moment(this.monthSelected).locale('it').format('MMMM YYYY');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  chunkDays(days: any[], size = 5): any[][] {
    const result = [];

    // Filtro i giorni, escludendo sabati (6) e domeniche (0)
    const filteredDays = days.filter(day => {
      const date = new Date(day.date); // 'day.date' è la stringa 'yyyy-MM-dd'
      const dayOfWeek = date.getDay();
      return dayOfWeek !== 0 && dayOfWeek !== 6;
    });

    // Suddivido l'array filtrato in blocchi di 'size'
    for (let i = 0; i < filteredDays.length; i += size) {
      result.push(filteredDays.slice(i, i + size));
    }

    return result;
  }

  isInSelectedMonth(dateStr: string): boolean {
    const date = new Date(dateStr);
    return date.getMonth() === this.monthSelected.getMonth();
  }

  getIconRequestUi(type: string) {
    return (
      CALENDAR_REQUEST_UI[type as CalendarRequestType] ?? DEFAULT_REQUEST_UI
    );
  }

  // funzione per capire se siamo sabato o domenica
  isWeekend(date: string | Date): boolean {
    const day = new Date(date).getDay(); // 0 = domenica, 6 = sabato
    return day === 0 || day === 6;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
